import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { emailRegex, FormState, phoneRegex } from '@opu/utils';
import AxiosInstance from '../../../helpers/AxiosInstance';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import { auth } from '../../../firebase-init';

const useForm = (user) => {
  const { t, i18n } = useTranslation();
  let history = useHistory();

  const [isEmailVerified, setIsEmailVerified] = useState(null);
  const [accountVerificationEmailSent, setAccountVerificationEmailSent] = useState(false);

  const fetchUserData = async () => {
    console.log('🚀 Profile: fetchUserData() called');
    console.log('🔍 Profile: auth.currentUser.uid:', auth.currentUser?.uid);
    
    try {
      console.log('📡 Profile: Making API call to /auth/get-authenticated-user/' + auth.currentUser.uid);
      const response = await AxiosInstance.get(`/auth/get-authenticated-user/${auth.currentUser.uid}`);
      console.log('✅ Profile: User data API call successful');
      console.log('✅ Profile: Response data:', response.data);
      console.log('✅ Profile: Student data:', response.data?.student);
      
      if (response.data && response.data.student) {
        console.log('✅ Profile: Student data found, updating user object');
        // Update the user object with student data
        const updatedUser = {
          ...user,
          ...response.data,
          student: response.data.student
        };
        console.log('✅ Profile: Updated user object:', updatedUser);
        
        // Now call getProfile with the updated user data
        getProfileWithUser(updatedUser);
      } else {
        console.log('❌ Profile: No student data found in API response');
        Swal.fire(t('student.ERROR_GET_PROFILE'), '', 'error');
        history.push('/');
      }
    } catch (error) {
      console.log('❌ Profile: Error fetching user data:', error);
      Swal.fire(t('student.ERROR_GET_PROFILE'), '', 'error');
      history.push('/');
    }
  };

  const getProfileWithUser = (userData) => {
    console.log('🚀 Profile: getProfileWithUser() called');
    console.log('🔍 Profile: userData received:', userData);
    console.log('🔍 Profile: userData.student:', userData?.student);
    console.log('🔍 Profile: userData.id:', userData?.id);
    
    // Get the student ID from the user object
    const studentId = userData.student?.id || userData.id;
    console.log('🔍 Profile: Using student ID:', studentId);
    console.log('🔍 Profile: API endpoint will be: /student/' + studentId);
    
    console.log('📡 Profile: Making API call...');
    AxiosInstance.get('/student/' + studentId)
      .then((response) => {
        console.log('✅ Profile: API call successful');
        console.log('✅ Profile: Response status:', response.status);
        console.log('✅ Profile: Response data:', response.data);
        console.log('✅ Profile: Response data.user:', response.data?.user);
        
        let inputValues = {
          firstName: response.data.user.firstName,
          lastName: response.data.user.lastName,
          gender: response.data.user.gender,
          bac_matricule: response.data.user.bac_matricule,
          phone: response.data.user.phone,
          email: response.data.user.email,
        };
        console.log('✅ Profile: Input values prepared:', inputValues);
        updateFormState(inputValues);
        console.log('✅ Profile: Form state updated successfully');
      })
      .catch((error) => {
        console.log('❌ Profile: API call failed');
        console.log('❌ Profile: Error object:', error);
        console.log('❌ Profile: Error response:', error.response);
        console.log('❌ Profile: Error status:', error.response?.status);
        console.log('❌ Profile: Error data:', error.response?.data);
        console.log('❌ Profile: Error message:', error.message);
        Swal.fire(t('student.ERROR_GET_PROFILE'), '', 'error');
        history.push('/');
      });
  };

  const getProfile = () => {
    console.log('🚀 Profile: getProfile() called');
    console.log('🔍 Profile: user object received:', user);
    console.log('🔍 Profile: user.student:', user?.student);
    console.log('🔍 Profile: user.id:', user?.id);
    
    // Get the student ID from the user object
    const studentId = user.student?.id || user.id;
    console.log('🔍 Profile: Using student ID:', studentId);
    console.log('🔍 Profile: API endpoint will be: /student/' + studentId);
    
    console.log('📡 Profile: Making API call...');
    AxiosInstance.get('/student/' + studentId)
      .then((response) => {
        console.log('✅ Profile: API call successful');
        console.log('✅ Profile: Response status:', response.status);
        console.log('✅ Profile: Response data:', response.data);
        console.log('✅ Profile: Response data.user:', response.data?.user);
        
        let inputValues = {
          firstName: response.data.user.firstName,
          lastName: response.data.user.lastName,
          gender: response.data.user.gender,
          bac_matricule: response.data.user.bac_matricule,
          phone: response.data.user.phone,
          email: response.data.user.email,
        };
        console.log('✅ Profile: Input values prepared:', inputValues);
        updateFormState(inputValues);
        console.log('✅ Profile: Form state updated successfully');
      })
      .catch((error) => {
        console.log('❌ Profile: API call failed');
        console.log('❌ Profile: Error object:', error);
        console.log('❌ Profile: Error response:', error.response);
        console.log('❌ Profile: Error status:', error.response?.status);
        console.log('❌ Profile: Error data:', error.response?.data);
        console.log('❌ Profile: Error message:', error.message);
        Swal.fire(t('student.ERROR_GET_PROFILE'), '', 'error');
        history.push('/');
      });
  };

  useEffect(() => {
    console.log('🚀 Profile: useEffect triggered');
    console.log('🔍 Profile: auth.currentUser:', auth.currentUser);
    console.log('🔍 Profile: user prop:', user);
    
    if (auth.currentUser) {
      setIsEmailVerified(auth.currentUser.emailVerified);
    }
    
    // If user object is not provided or doesn't have student data, fetch it
    if (!user || !user.student) {
      console.log('🔍 Profile: User object not provided or missing student data, fetching from API');
      fetchUserData();
    } else {
      console.log('🔍 Profile: User object has student data, calling getProfile');
      getProfile();
    }
  }, [user]);

  const sendPasswordResetEmail = () => {
    if (auth.currentUser) {
      console.log('🔍 Profile: Sending password reset email to:', auth.currentUser.email);
      AxiosInstance.post(`/auth/send-password-recovery-email`, {
        email: auth.currentUser.email,
        firebase_uid: auth.currentUser.uid,
      })
        .then(() => {
          console.log('✅ Profile: Password reset email sent successfully');
          Swal.fire(t('profile.password-reset-email-sent'), '', 'success');
        })
        .catch((error) => {
          console.error('❌ Profile: Error sending password reset email:', error);
          Swal.fire(t('profile.error-send-password-reset-email'), error.message || '', 'error');
        });
    } else {
      console.error('❌ Profile: No current user found');
      Swal.fire(t('profile.error-send-password-reset-email'), '', 'error');
    }
  };

  const updateProfile = () => {
    // Get the student ID from the user object
    const studentId = user?.student?.id || user?.id;
    if (!studentId) {
      console.error('❌ Profile: No student ID found for update');
      Swal.fire(t('profile.ERROR_UPDATE_PROFILE'), '', 'error');
      return;
    }
    
    AxiosInstance.patch('/student/update-profile/' + studentId, {
      firstName: formState.inputValues.firstName,
      lastName: formState.inputValues.lastName,
      email: formState.inputValues.email,
      phone: formState.inputValues.phone,
      bac_matricule: formState.inputValues.bac_matricule,
      gender: formState.inputValues.gender,
    })
      .then((res) => {
        Swal.fire(t('profile.SUCCESS_UPDATE_PROFILE'), '', 'success');
      })
      .catch((error) => {
        if (error.status === 400) {
          handleErrorsServer(error.data.message);
        }
        Swal.fire(t('profile.ERROR_UPDATE_PROFILE'), '', 'error');
      });
  };

  const sendAccountVerificationEmail = (e) => {
    e.preventDefault();
    AxiosInstance.post('/auth/send-account-verification-email/')
      .then((res) => {
        Swal.fire(t('SEND_ACCOUNT_VERIFICATION_EMAIL_SUCCESS'), '', 'success');
        setAccountVerificationEmailSent(true);
      })
      .catch((error) => {
        Swal.fire(t('SEND_ACCOUNT_VERIFICATION_EMAIL_FAILED'), '', 'error');
      });
  };

  const inputValues = {
    firstName: { value: '', validate: { required: true } },
    lastName: { value: '', validate: { required: true } },
    phone: { value: '', validate: { required: false, pattern: phoneRegex } },
    gender: { value: '', validate: { required: true } },
    bac_matricule: { value: '', validate: { required: false } },
    email: { value: '', validate: { required: false, pattern: emailRegex } },
  };

  const { formState, submitHandler, updateFormState, resetFormState, loading, field, handleErrorsServer } = FormState(
    inputValues,
    updateProfile
  );

  return {
    submitHandler,
    resetFormState,
    loading,
    field,
    sendPasswordResetEmail,
    isEmailVerified,
    sendAccountVerificationEmail,
    accountVerificationEmailSent,
  };
};

export default useForm;
